"use client";

import { useQuery } from "@tanstack/react-query";
import { getToken } from "./get-tokens-and-chains";
import { Token } from "@lifi/sdk";

interface FeaturedProject {
  id: string;
  tokenAddress: string;
  chainId: number;
  displayOrder: number;
  tokenName: string | null;
  tokenSymbol: string | null;
  tokenLogoURI: string | null;
}

interface FeaturedProjectsResponse {
  dbProjects: FeaturedProject[];
  formattedProjects: Token[];
}

export function useAllFeaturedProjects() {
  return useQuery<FeaturedProjectsResponse>({
    queryKey: ["featured-projects"],
    staleTime: 60 * 1000, // 60 seconds - prevents constant refetches that trigger balance polling
    queryFn: async () => {
      const res = await fetch("/api/dashboard/featured-tokens");
      const result = await res.json();

      if (!result.success || !result.data) {
        return { dbProjects: [], formattedProjects: [] };
      }

      const SOLANA_CHAIN_ID = 1151111081099710;

      const formattedProjects = await Promise.all(
        result.data.map(async (project: FeaturedProject) => {
          try {
            // Try to fetch from LiFi SDK for accurate decimals (works for both Solana and EVM)
            const lifiToken = await getToken(project.chainId, project.tokenAddress);
            if (lifiToken) {
              // Logo priority: DB (pre-resolved) → LiFi (fallback for legacy tokens) → empty
              lifiToken.logoURI = project.tokenLogoURI || lifiToken.logoURI || "";
              if (process.env.NODE_ENV !== "production") {
                console.log("[Featured Projects] Token from LiFi:", {
                  symbol: lifiToken.symbol,
                  decimals: lifiToken.decimals,
                  chainId: project.chainId,
                });
              }
              return lifiToken;
            }

            // Fallback for Solana tokens if LiFi lookup fails
            if (project.chainId === SOLANA_CHAIN_ID) {
              if (process.env.NODE_ENV !== "production") {
                console.log("[Featured Projects] Solana fallback (no LiFi):", {
                  symbol: project.tokenSymbol,
                  decimals: 9,
                });
              }
              return {
                address: project.tokenAddress,
                chainId: project.chainId,
                symbol: project.tokenSymbol || "TOKEN",
                decimals: 9, // Fallback - most SPL tokens are 6 or 9, but LiFi should have correct value
                name: project.tokenName || "Token",
                logoURI: project.tokenLogoURI || "",
                priceUSD: "0",
              } as Token;
            }

            return null;
          } catch (error) {
            console.warn("[Featured Projects] Error fetching token:", project.tokenAddress, error);
            return null;
          }
        })
      );

      return {
        dbProjects: result.data,
        formattedProjects: formattedProjects.filter(
          (project): project is Token => project !== null
        ),
      };
    },
  });
}
